<script lang="ts">
    import EntitySelector from '$lib/components/common/selectors/EntitySelector.svelte';
    import Button from '$lib/components/common/button/Button.svelte';
    import RequiredBadge from '$lib/components/common/badges/RequiredBadge.svelte';
    import {showToast} from '$lib/helpers/toastrHelper';
    import {fetchWithAuth} from '$lib/utils/http/httpUtil';
    import {Eye, EyeSlash, Info} from 'phosphor-svelte';
    import {popup} from '@skeletonlabs/skeleton';
    import Tooltip from '$lib/components/common/tooltip/Tooltip.svelte';
    import {GetHoverPopupSettings} from '$lib/components/common/tooltip/utils';

    export let clientId: string = '';
  export let entityId: string = '';
  export let clientName: string = '';
  export let organisationId: string | undefined;

  interface TechOneEntityConfig {
    techOneRemoteUrl: string;
    techOneClientId: string;
    techOneClientSecret: string;
    techOneSourceIdentifier: string;
    techOneSourceIdentifierName: string;
    techOneLedgerCode: string;
    financialYearEndMonth: number;
    budgetStartDate: string | null;
    budgetEndDate: string | null;
  }
  interface TechOneExistingConfigResponse {
    exists: boolean;
    config: TechOneEntityConfig | null;
  }

  const SECRET_PLACEHOLDER = '••••••';

  const sourceIdentifierTooltip = GetHoverPopupSettings('techone_source_identifier_info');
  const sourceIdentifierNameTooltip = GetHoverPopupSettings('techone_source_identifier_name_info');
  const ledgerCodeTooltip = GetHoverPopupSettings('techone_ledger_code_info');

  const MONTHS = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  let selectedEntityId = entityId;
  let checkedEntityId = '';
  let isLoading = false;
  let isSaving = false;
  let exists = false;
  let showSecret = false;

  let techOneRemoteUrl = '';
  let techOneClientId = '';
  let techOneClientSecret = '';
  let techOneSourceIdentifier = '';
  let techOneSourceIdentifierName = '';
  let techOneLedgerCode = '';
  let financialYearEndMonth = 12;
  let budgetStartDate = '';
  let budgetEndDate = '';

  $: secretIsHidden = techOneClientSecret === SECRET_PLACEHOLDER;
  $: if (selectedEntityId && selectedEntityId !== checkedEntityId) loadConfig(selectedEntityId);

  function resetForm() {
    techOneRemoteUrl = '';
    techOneClientId = '';
    techOneClientSecret = '';
    techOneSourceIdentifier = '';
    techOneSourceIdentifierName = '';
    techOneLedgerCode = '';
    financialYearEndMonth = 12;
    budgetStartDate = '';
    budgetEndDate = '';
    showSecret = false;
  }

  async function loadConfig(targetEntityId: string) {
    isLoading = true;
    try {
      const response = await fetchWithAuth(`/api/techone/config/${clientId}/${targetEntityId}`);
      const result: TechOneExistingConfigResponse = await response.json();
      if (selectedEntityId !== targetEntityId) return;

      exists = result.exists;
      if (result.config) {
        techOneRemoteUrl = result.config.techOneRemoteUrl;
        techOneClientId = result.config.techOneClientId;
        techOneClientSecret = result.config.techOneClientSecret;
        techOneSourceIdentifier = result.config.techOneSourceIdentifier;
        techOneSourceIdentifierName = result.config.techOneSourceIdentifierName;
        techOneLedgerCode = result.config.techOneLedgerCode;
        financialYearEndMonth = result.config.financialYearEndMonth;
        budgetStartDate = result.config.budgetStartDate?.slice(0, 10) ?? '';
        budgetEndDate = result.config.budgetEndDate?.slice(0, 10) ?? '';
      } else {
        resetForm();
      }
      checkedEntityId = targetEntityId;
    } catch {
      showToast({ title: 'Error', message: 'Failed to load TechOne configuration', type: 'error' });
    } finally {
      isLoading = false;
    }
  }

  function resetAll() {
    resetForm();
    selectedEntityId = '';
    checkedEntityId = '';
  }

  function validationError(): string | null {
    if (!techOneRemoteUrl.trim()) return 'Enter a remote URL';
    if (!techOneClientId.trim()) return 'Enter a client ID';
    if (!exists && !techOneClientSecret.trim()) return 'Enter a client secret';
    if (!techOneSourceIdentifier.trim()) return 'Enter a source identifier';
    if (!techOneLedgerCode.trim()) return 'Enter a ledger code';
    return null;
  }

  async function handleConnect() {
    const error = validationError();
    if (error) {
      showToast({ title: 'Error', message: error, type: 'error' });
      return;
    }

    isSaving = true;
    try {
      const response = await fetchWithAuth('/api/techone/setup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          clientId: Number(clientId),
          entityId: Number(selectedEntityId),
          techOneRemoteUrl,
          techOneClientId,
          techOneClientSecret,
          techOneSourceIdentifier,
          techOneSourceIdentifierName: techOneSourceIdentifierName.trim() || null,
          techOneLedgerCode,
        }),
      });
      const result = await response.json();
      if (!response.ok || result.success === false) {
        showToast({ title: 'Error', message: result.errorMessage ?? 'Failed to connect TechOne', type: 'error' });
        return;
      }
      showToast({ title: 'Connected', message: 'TechOne connected', type: 'success' });
      checkedEntityId = '';
      await loadConfig(selectedEntityId);
    } catch {
      showToast({ title: 'Error', message: 'Failed to connect TechOne', type: 'error' });
    } finally {
      isSaving = false;
    }
  }

  async function handleSave() {
    const error = validationError();
    if (error) {
      showToast({ title: 'Error', message: error, type: 'error' });
      return;
    }

    isSaving = true;
    try {
      const response = await fetchWithAuth(`/api/techone/config/${clientId}/${selectedEntityId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          techOneRemoteUrl,
          techOneClientId,
          techOneClientSecret: techOneClientSecret.trim() && techOneClientSecret !== SECRET_PLACEHOLDER ? techOneClientSecret : null,
          techOneSourceIdentifier,
          techOneSourceIdentifierName: techOneSourceIdentifierName.trim() || null,
          techOneLedgerCode,
          financialYearEndMonth,
          budgetStartDate: budgetStartDate || null,
          budgetEndDate: budgetEndDate || null,
        }),
      });
      const result = await response.json();
      if (!response.ok || result.success === false) {
        showToast({ title: 'Error', message: result.errorMessage ?? 'Failed to save TechOne configuration', type: 'error' });
        return;
      }
      showToast({ title: 'Saved', message: 'TechOne configuration saved', type: 'success' });
      checkedEntityId = '';
      await loadConfig(selectedEntityId);
    } catch {
      showToast({ title: 'Error', message: 'Failed to save TechOne configuration', type: 'error' });
    } finally {
      isSaving = false;
    }
  }
</script>

<div class="space-y-4 p-1">
  <EntitySelector bind:selectedEntityId {clientId} isRequired={true} />

  {#if !selectedEntityId}
    <div class="rounded-lg bg-gray-100 p-4 text-sm text-gray-600">Select an entity to configure TechOne.</div>
  {:else if isLoading}
    <div class="text-sm text-gray-500">Loading…</div>
  {:else}
    <div class="rounded-lg border bg-white p-4">
      <p class="mb-3 text-sm font-medium">{exists ? 'Connection' : 'Connect TechOne'}</p>
      {#if !exists}
        <p class="mb-4 text-sm text-gray-600">This entity has no TechOne configuration yet.</p>
      {/if}

      <label class="mb-2 block text-sm text-gray-600" for="techone-remote-url">
        Remote URL <RequiredBadge />
      </label>
      <input
        id="techone-remote-url"
        type="text"
        bind:value={techOneRemoteUrl}
        placeholder="https://tenant.t1cloud.com/T1Default/CiAnywhere/Web/PROD/"
        class="mb-3 w-full rounded-md border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
      />

      <div class="mb-3 grid grid-cols-2 gap-3">
        <div>
          <label class="mb-2 block text-sm text-gray-600" for="techone-client-id">Client ID <RequiredBadge /></label>
          <input
            id="techone-client-id"
            type="text"
            bind:value={techOneClientId}
            placeholder="OAuth client ID"
            class="w-full rounded-md border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
        <div>
          <label class="mb-2 block text-sm text-gray-600" for="techone-client-secret">
            Client secret {#if !exists}<RequiredBadge />{/if}
          </label>
          {#if secretIsHidden}
            <div class="flex items-center gap-2">
              <div class="flex-1 rounded-md border border-gray-300 bg-gray-100 px-3 py-2 text-sm italic text-gray-500">Secret is set but not visible</div>
              <Button
                label="Change"
                onClick={() => {
                  techOneClientSecret = '';
                  showSecret = false;
                }}
                colour="secondary"
                size="small"
              />
            </div>
          {:else}
            <div class="relative">
              {#if showSecret}
                <input
                  id="techone-client-secret"
                  type="text"
                  bind:value={techOneClientSecret}
                  placeholder="OAuth client secret"
                  class="w-full rounded-md border px-3 py-2 pr-10 focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              {:else}
                <input
                  id="techone-client-secret"
                  type="password"
                  bind:value={techOneClientSecret}
                  placeholder="OAuth client secret"
                  class="w-full rounded-md border px-3 py-2 pr-10 focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              {/if}
              <button
                type="button"
                class="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                on:click={() => (showSecret = !showSecret)}
              >
                {#if showSecret}<EyeSlash size={20} />{:else}<Eye size={20} />{/if}
              </button>
            </div>
          {/if}
        </div>
      </div>

      <div class="grid grid-cols-2 gap-3">
        <div>
          <label class="mb-2 flex items-center gap-2 text-sm text-gray-600" for="techone-source-identifier">
            Source identifier <RequiredBadge />
            <button type="button" class="[&>*]:pointer-events-none" use:popup={sourceIdentifierTooltip}>
              <Info size={16} />
            </button>
          </label>
          <input
            id="techone-source-identifier"
            type="text"
            bind:value={techOneSourceIdentifier}
            placeholder="AP processing rule set GUID"
            class="w-full rounded-md border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
        <div>
          <label class="mb-2 flex items-center gap-2 text-sm text-gray-600" for="techone-source-identifier-name">
            Source identifier name
            <button type="button" class="[&>*]:pointer-events-none" use:popup={sourceIdentifierNameTooltip}>
              <Info size={16} />
            </button>
          </label>
          <input
            id="techone-source-identifier-name"
            type="text"
            bind:value={techOneSourceIdentifierName}
            placeholder="Alii"
            class="w-full rounded-md border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
      </div>

      <div class="mt-3 grid grid-cols-2 gap-3">
        <div>
          <label class="mb-2 flex items-center gap-2 text-sm text-gray-600" for="techone-ledger-code">
            Ledger code <RequiredBadge />
            <button type="button" class="[&>*]:pointer-events-none" use:popup={ledgerCodeTooltip}>
              <Info size={16} />
            </button>
          </label>
          <input
            id="techone-ledger-code"
            type="text"
            bind:value={techOneLedgerCode}
            placeholder="e.g. P1"
            class="w-full rounded-md border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
      </div>
    </div>

    {#if exists}
      <div class="rounded-lg border bg-white p-4">
        <p class="mb-3 text-sm font-medium">Financial settings</p>
        <div class="grid grid-cols-3 gap-3">
          <div>
            <label class="mb-2 block text-sm text-gray-600" for="techone-fy-end-month">Financial year end month</label>
            <select id="techone-fy-end-month" bind:value={financialYearEndMonth} class="w-full rounded-md border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500">
              {#each MONTHS as month, index}
                <option value={index + 1}>{month}</option>
              {/each}
            </select>
          </div>
          <div>
            <label class="mb-2 block text-sm text-gray-600" for="techone-budget-start">Budget start date</label>
            <input
              id="techone-budget-start"
              type="date"
              bind:value={budgetStartDate}
              class="w-full rounded-md border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label class="mb-2 block text-sm text-gray-600" for="techone-budget-end">Budget end date</label>
            <input
              id="techone-budget-end"
              type="date"
              bind:value={budgetEndDate}
              class="w-full rounded-md border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
        </div>
      </div>
    {/if}

    <div class="flex justify-end gap-3">
      <Button label="Reset All" onClick={resetAll} disabled={isSaving} colour="secondary" />
      <Button
        label={isSaving ? 'Saving...' : exists ? 'Update Configuration' : 'Save Configuration'}
        onClick={exists ? handleSave : handleConnect}
        disabled={isSaving}
      />
    </div>
  {/if}
</div>

<Tooltip config={sourceIdentifierTooltip}>
  TechOne's AP Processing Rule Set identifier for posting invoices and credit notes — a GUID specific to this T1 environment. TechOne provides this when the integration is set up on their side.
</Tooltip>

<Tooltip config={sourceIdentifierNameTooltip}>
  Identifies Alii as the source system to TechOne, sent alongside the source identifier when posting invoices. Defaults to Alii — only change this if TechOne has specified something different for this tenant.
</Tooltip>

<Tooltip config={ledgerCodeTooltip}>
  The ledger code TechOne uses for both the AP header line and every GL dissection line when posting invoices, expense claims, and statements (e.g. P1, P2). Ask TechOne support for the correct value for this tenant — posting is refused until this is set.
</Tooltip>
